import { Injectable } from '@angular/core';
import {
  NavigationEnd,
  Router,
  UrlSegmentGroup,
  UrlTree,
} from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';

export type BrandVariant = 'default' | 'comojubilar';

@Injectable({
  providedIn: 'root',
})
export class BrandingService {
  private readonly defaultBrand: BrandVariant = 'default';
  private readonly brandedPath = 'campana-comojubilar';
  private readonly brandSubject = new BehaviorSubject<BrandVariant>(
    this.defaultBrand
  );
  private readonly assetOverrides: Record<
    BrandVariant,
    Record<string, string>
  > = {
    default: {},
    comojubilar: {
      'assets/images/icon-tooltip.svg':
        'assets/images/icon-tooltip-comojubilar.svg',
      'assets/images/arrow-select.svg':
        'assets/images/arrow-select-comojubilar.svg',
      'assets/images/arrow-down.svg':
        'assets/images/arrow-down-comojubilar.svg',
      'assets/images/Infinity-8.3s-200px.svg':
        'assets/images/Infinity-8.3s-200px-comojubilar.svg',
      'assets/images/error-404.svg': 'assets/images/error-404-comojubilar.svg',
      'assets/images/logo_home.svg': 'assets/images/logo-comojubilar.webp',
    },
  };

  readonly brand$ = this.brandSubject.asObservable();

  constructor(private router: Router) {
    this.applyBrandFromUrl(this.router.url);

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) =>
        this.applyBrandFromUrl(event.urlAfterRedirects)
      );
  }

  initialize(): void {
    // Método intencionalmente vacío para forzar la inicialización cuando se inyecta.
  }

  get currentBrand(): BrandVariant {
    return this.brandSubject.value;
  }

  getAssetPath(original: string): string {
    const normalized = this.normalizeAssetPath(original);
    const override = this.assetOverrides[this.currentBrand][normalized];
    return override ?? normalized;
  }

  get routePrefix(): string {
    return this.currentBrand === 'comojubilar' ? this.brandedPath : '';
  }

  withBrandPrefix(commands: string[]): string[] {
    const sanitizedCommands = commands.filter((command) =>
      Boolean(command && command.trim() !== '')
    );

    const baseSegments = this.routePrefix ? ['/', this.routePrefix] : ['/'];

    if (sanitizedCommands.length === 0) {
      return baseSegments;
    }

    const firstCommand = sanitizedCommands[0];

    if (firstCommand.startsWith('/')) {
      return sanitizedCommands;
    }

    return [...baseSegments, ...sanitizedCommands];
  }

  normalizeUrl(url: string): string {
    const tree: UrlTree = this.router.parseUrl(url);
    const primary: UrlSegmentGroup | undefined = tree.root.children['primary'];

    if (!primary) {
      return url;
    }

    const segments = primary.segments.map((segment) => segment.path);

    if (segments[0] === this.brandedPath) {
      const remainder = segments.slice(1).join('/');
      return remainder ? `/${remainder}` : '/';
    }

    return url; // Already normalized
  }

  private applyBrandFromUrl(url: string): void {
    const brand = this.detectBrand(url);
    if (brand !== this.brandSubject.value) {
      this.brandSubject.next(brand);
      this.updateBodyClass(brand);
    }
  }

  private normalizeAssetPath(source: string): string {
    if (!source) {
      return source;
    }

    const cleaned = source.replace(/^(\.\/|\.\.\/)+/, '');
    if (cleaned.startsWith('assets/')) {
      return cleaned;
    }

    if (cleaned.startsWith('/assets/')) {
      return cleaned.slice(1);
    }

    return `assets/${cleaned}`;
  }

  private detectBrand(url: string): BrandVariant {
    const tree: UrlTree = this.router.parseUrl(url);
    const primary: UrlSegmentGroup | undefined = tree.root.children['primary'];

    if (!primary || primary.segments.length === 0) {
      return this.defaultBrand;
    }

    return primary.segments[0].path === this.brandedPath
      ? 'comojubilar'
      : this.defaultBrand;
  }

  private updateBodyClass(brand: BrandVariant): void {
    const className = 'theme-comojubilar';
    if (brand === 'comojubilar') {
      document.body.classList.add(className);
    } else {
      document.body.classList.remove(className);
    }
  }
}
